/**
 * Amazon marketplace ids → SP-API region and Seller Central host.
 *
 * Lives in the shared package because both sides need the same answer: the API
 * routes a grant by region, and onboarding groups Ads profiles into the
 * `(merchantId, region)` pairs the API will then validate. A second table would
 * drift, and a drifted region silently groups a profile into an authorization
 * the backend rejects.
 *
 * Ids and hosts come from Amazon's Marketplace IDs and Seller Central URLs
 * references. Keep this mapping id-based: display country names are neither
 * stable nor unique identifiers.
 */
import type { SpApiRegion } from "./schemas/sp-api";

export interface AmazonMarketplace {
  region: SpApiRegion;
  sellerCentralBaseUrl: string;
}

export const AMAZON_MARKETPLACES: Record<string, AmazonMarketplace> = {
  // North America and Brazil
  A2EUQ1WTGCTBG2: { region: "na", sellerCentralBaseUrl: "https://sellercentral.amazon.ca" },
  ATVPDKIKX0DER: { region: "na", sellerCentralBaseUrl: "https://sellercentral.amazon.com" },
  A1AM78C64UM0Y8: { region: "na", sellerCentralBaseUrl: "https://sellercentral.amazon.com.mx" },
  A2Q3Y263D00KWC: { region: "na", sellerCentralBaseUrl: "https://sellercentral.amazon.com.br" },

  // Europe, Middle East, India, and Africa
  A28R8C7NBKEWEA: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.ie" },
  A1RKKUPIHCS9HS: { region: "eu", sellerCentralBaseUrl: "https://sellercentral-europe.amazon.com" },
  A1F83G8C2ARO7P: { region: "eu", sellerCentralBaseUrl: "https://sellercentral-europe.amazon.com" },
  A13V1IB3VIYZZH: { region: "eu", sellerCentralBaseUrl: "https://sellercentral-europe.amazon.com" },
  AMEN7PMS3EDWL: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.com.be" },
  A1805IZSGTT6HS: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.nl" },
  A1PA6795UKMFR9: { region: "eu", sellerCentralBaseUrl: "https://sellercentral-europe.amazon.com" },
  APJ6JRA9NG5V4: { region: "eu", sellerCentralBaseUrl: "https://sellercentral-europe.amazon.com" },
  A2NODRKZP88ZB9: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.se" },
  AE08WJ6YKNBMC: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.co.za" },
  A1C3SOZRARQ6R3: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.pl" },
  ARBP9OOSHTCHU: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.eg" },
  A33AVAJ2PDY3EV: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.com.tr" },
  A17E79C6D8DWNP: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.sa" },
  A2VIGQ35RCS4UG: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.ae" },
  A21TJRUUN4KGV: { region: "eu", sellerCentralBaseUrl: "https://sellercentral.amazon.in" },

  // Far East
  A19VAU5U5O7RUS: { region: "fe", sellerCentralBaseUrl: "https://sellercentral.amazon.sg" },
  A39IBJ37TRP1C6: { region: "fe", sellerCentralBaseUrl: "https://sellercentral.amazon.com.au" },
  A1VC38T7YXB528: { region: "fe", sellerCentralBaseUrl: "https://sellercentral.amazon.co.jp" },
};

/** The marketplace's SP-API region, or null when SP-API does not support it. */
export function spApiRegionForMarketplace(marketplaceId: string): SpApiRegion | null {
  return AMAZON_MARKETPLACES[marketplaceId]?.region ?? null;
}
